/**
 * Acknowledge, dismiss and act on an incident (ADR 0058), through the hub module 'watch'. In
 * demo mode they change the demo data in place. The hub's SSE update replaces state.ext.watch,
 * so every surface follows; the answer is applied at once too, so the row moves on the click.
 */
import type { Incident, IncidentState, WatchState } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { watchStateOf } from './model.ts';

/** What the hub answers: the incident, the whole state, or both. */
interface WatchAnswer {
  incident?: Incident;
  state?: WatchState;
}

function current(): WatchState | null {
  return watchStateOf(useOrgus.getState().ext);
}

function setWatch(data: WatchState): void {
  useOrgus.getState().applyExt({ ext: 'watch', type: 'state', data });
}

/** Apply an answer: a full state replaces the slice; a lone incident replaces its row. */
export function applyAnswer(r: WatchAnswer | null | undefined): void {
  if (!r) return;
  if (r.state && Array.isArray(r.state.incidents)) return setWatch(r.state);
  const cur = current();
  if (r.incident && cur) setWatch({ ...cur, incidents: cur.incidents.map((i) => (i.id === r.incident!.id ? r.incident! : i)) });
}

const mock = () => useOrgus.getState().mode === 'mock';

/** Demo mode: move one incident to a new state, as the hub would. */
function mockMove(id: string, state: IncidentState, extra: Partial<Incident> = {}): Incident {
  const cur = current();
  const found = cur?.incidents.find((i) => i.id === id);
  if (!cur || !found) throw new Error('That incident is gone. The watch resolved it or it was dismissed.');
  const now = Date.now();
  const next: Incident = { ...found, state, by: 'owner', updatedAt: now, ...extra };
  setWatch({ ...cur, incidents: [next, ...cur.incidents.filter((i) => i.id !== id)] });
  return next;
}

export async function ackIncident(id: string, note?: string): Promise<void> {
  if (mock()) return void mockMove(id, 'acknowledged', note ? { note } : {});
  applyAnswer(await api<WatchAnswer>(`/api/watch/${encodeURIComponent(id)}/ack`, { method: 'POST', body: note ? { note } : {} }));
}

/** Dismiss with a reason (required: the hub refuses an empty one). */
export async function dismissIncident(id: string, reason: string): Promise<void> {
  const why = reason.trim();
  if (!why) throw new Error('Say why you dismiss it, so the next person knows.');
  if (mock()) return void mockMove(id, 'dismissed', { note: why, resolvedAt: Date.now(), resolvedBy: 'owner' });
  applyAnswer(await api<WatchAnswer>(`/api/watch/${encodeURIComponent(id)}/dismiss`, { method: 'POST', body: { reason: why } }));
}

/** Run the incident's own action on the hub (a hand-back of every waiting request on its machine). */
export async function actOnIncident(id: string): Promise<void> {
  if (mock()) return void mockMove(id, 'fixing', { note: 'Passed the waiting requests back to their sessions.' });
  applyAnswer(await api<WatchAnswer>(`/api/watch/${encodeURIComponent(id)}/act`, { method: 'POST', body: {} }));
}

/** Read the watch once, when the first state came without it (an older hub, or the module is off). */
export async function loadWatch(): Promise<WatchState> {
  const r = await api<WatchState | { state: WatchState }>('/api/watch');
  const state = 'state' in r && r.state ? r.state : (r as WatchState);
  setWatch(state);
  return state;
}
